import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import ErrorIcon from "@mui/icons-material/Error";
import { SparklesIcon, Typography } from "@avenue-8/sidekick-ui";
import { FadingText } from "@shared/components/FadingText";
import { useCrmSyncState } from "../hooks/useCrmSyncState";

const STORAGE_KEY = "@sidekick:contacts-processing-start";
const CHECK_INTERVAL_MS = 30_000;

// Temporary loading states
const MESSAGES: { text: string; minElapsedMs: number }[] = [
  { text: "Scanning your inbox for contacts", minElapsedMs: 0 },
  {
    text: "Identifying contacts and leads from your emails",
    minElapsedMs: 2 * 60 * 1000,
  },
  {
    text: "Getting everything ready, come back anytime",
    minElapsedMs: 5 * 60 * 1000,
  },
];

function getOrInitStartTs(): number {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    const now = Date.now();
    if (!stored || stored > now) {
      localStorage.setItem(STORAGE_KEY, String(now));
      return now;
    }
    return stored;
  } catch {
    return Date.now();
  }
}

function getMessageIndex(startTs: number): number {
  const elapsed = Date.now() - startTs;
  let index = 0;
  for (let i = 0; i < MESSAGES.length; i++) {
    if (elapsed >= MESSAGES[i].minElapsedMs) index = i;
  }
  return index;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem 1.5rem;
  text-align: center;
  max-width: 480px;
  margin: 0 auto;
`;

const StatusCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem 1.5rem;
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 0.25rem;
  width: 100%;
`;

const IconCircle = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.background5};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ContactsProcessingState = () => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncState = useCrmSyncState({ enabled: true });
  const syncError = syncState.status === 'idle' && syncState.hasErrors;

  const [messageIndex, setMessageIndex] = useState(() =>
    getMessageIndex(getOrInitStartTs()),
  );

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setMessageIndex(getMessageIndex(getOrInitStartTs()));
    }, CHECK_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <Container>
      <div>
        <Typography variant="h5">
          {syncError ? "Import ran into an issue" : "Importing your contacts"}
        </Typography>
        {!syncError && <Typography color="text.secondary">
          This could take some time. Feel free to check back later.
        </Typography>}
      </div>
      <StatusCard>
        <IconCircle>
          {syncError ? (
            <ErrorIcon sx={{ color: "error.main" }} />
          ) : (
            <SparklesIcon animated />
          )}
        </IconCircle>
        {syncError ? (
          <>
            <Typography color="text.secondary">
              We had some trouble importing your contacts. Please try refreshing the page in a little while.
            </Typography>
            <Typography color="text.secondary">
              If the issue persists, please reach out to support.
            </Typography>
          </>
        ) : (
          <FadingText text={MESSAGES[messageIndex].text} animate />
        )}
      </StatusCard>
    </Container>
  );
};
